/**
 * OPFS writer with Safari createSyncAccessHandle fallback.
 *
 * Why this exists:
 *   Chromium / Firefox / Safari 17+ expose `FileSystemFileHandle.createWritable()`
 *   on OPFS handles, which is async and main-thread-friendly. Safari 15.2–16.x
 *   exposes only `createSyncAccessHandle()`, which is SYNCHRONOUS and forbidden
 *   on the main thread (throws InvalidStateError on write/close). Callers that
 *   write to OPFS via `await fh.createWritable()` therefore throw immediately on
 *   older Safari with `fh.createWritable is not a function` and silently fall
 *   back to in-memory blobs — losing the disk-backed protection.
 *
 *   /join's tree-merge pipeline relies on disk-backed intermediate FLACs to
 *   survive Safari's eager-memory-eviction. Telemetry 30 d: 118 Safari users
 *   hit `opfs_intermediate_failed` with this exact error → 100% rate for Safari
 *   tree-merge runs. This module closes that gap.
 *
 * Design:
 *   - Single inline Worker (bundled as a Blob URL on first use, ONE fetch, ONE
 *     URL cached for the session). Worker owns the syncAccessHandle, writes
 *     chunk-by-chunk into OPFS, closes it. Main thread postMessage's the source
 *     blob, awaits "done".
 *   - File is sliced in 4 MB chunks and the Workers reads via `.arrayBuffer()`
 *     per chunk — keeps memory bounded for big inputs (Safari memory budget
 *     is small + browser's MessageChannel imposes its own limits).
 *   - Mode auto-detection: prefers `createWritable` (faster, async, no Worker
 *     hop) when available. Falls back to the Worker+sync-handle path only on
 *     Safari < 17. Modern Safari hits the fast path.
 *
 * What this is NOT:
 *   - Not a generic FSA helper for showSaveFilePicker/showDirectoryPicker —
 *     those return handles that are NOT OPFS-backed; createSyncAccessHandle
 *     doesn't exist on them. Use `window.__FsaSink` (public/js/fsa-writable-fallback.js)
 *     for user-picker flows.
 *   - Not designed for streaming many small writes at arbitrary offsets
 *     (e.g. mp4-muxer's FileSystemWritableFileStreamTarget pattern). That
 *     needs a different shim that buffers writes by offset.
 *
 * API on window.__OpfsWriter:
 *   .detectMode() → 'createWritable' | 'syncAccessHandle' | 'none'
 *   .writeBlob(dirHandle, fileName, blob) → Promise<void>  — writes blob into OPFS
 *   .__version
 */
(function (w) {
    'use strict';

    if (w.__OpfsWriter && w.__OpfsWriter.__fromTimbricaShared) return;

    var CHUNK_BYTES = 4 * 1024 * 1024; // 4 MB; safe for postMessage transferables
    var _workerUrlCache = null;

    function _detectMode() {
        if (typeof FileSystemFileHandle === 'undefined') return 'none';
        var proto = FileSystemFileHandle.prototype;
        if (proto && typeof proto.createWritable === 'function') return 'createWritable';
        if (proto && typeof proto.createSyncAccessHandle === 'function') return 'syncAccessHandle';
        return 'none';
    }

    function _ensureWorkerUrl() {
        if (_workerUrlCache) return _workerUrlCache;
        // Worker source — note: lives in OPFS context; can't import outside libs.
        var src = '"use strict";\n'
            + 'self.onmessage = function (e) {\n'
            + '  var msg = e.data || {};\n'
            + '  if (msg.type !== "writeBlob") return;\n'
            + '  (async function () {\n'
            + '    var ah = null;\n'
            + '    try {\n'
            + '      var root = await navigator.storage.getDirectory();\n'
            + '      // Walk path segments — caller passes dirSegments[] so we mirror the main-thread dir tree.\n'
            + '      var dir = root;\n'
            + '      for (var i = 0; i < msg.dirSegments.length; i++) {\n'
            + '        dir = await dir.getDirectoryHandle(msg.dirSegments[i], { create: true });\n'
            + '      }\n'
            + '      var fh = await dir.getFileHandle(msg.fileName, { create: true });\n'
            + '      // Sync handle is only available in Worker context. open + write + close.\n'
            + '      ah = await fh.createSyncAccessHandle();\n'
            + '      ah.truncate(0);\n'
            + '      var offset = 0;\n'
            + '      // Use the slice protocol — caller streams chunks via repeated postMessage.\n'
            + '      // For simplicity in v1 we accept one full ArrayBuffer per write; chunking lives on main side.\n'
            + '      if (msg.chunks && msg.chunks.length) {\n'
            + '        for (var c = 0; c < msg.chunks.length; c++) {\n'
            + '          var chunk = msg.chunks[c];\n'
            + '          ah.write(chunk, { at: offset });\n'
            + '          offset += chunk.byteLength;\n'
            + '        }\n'
            + '      }\n'
            + '      ah.flush();\n'
            + '      ah.close();\n'
            + '      ah = null;\n'
            + '      self.postMessage({ ok: true, bytes: offset });\n'
            + '    } catch (err) {\n'
            + '      if (ah) { try { ah.close(); } catch (_) {} }\n'
            + '      self.postMessage({ ok: false, error: (err && err.message) || String(err) });\n'
            + '    }\n'
            + '  })();\n'
            + '};\n';
        _workerUrlCache = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
        return _workerUrlCache;
    }

    // Resolve the path from OPFS root to the given directory handle so the
    // Worker can re-open the same dir. Returns an array of segment names.
    // Browsers expose this via `root.resolve(handle)` which returns the
    // segment array (or null if not under root).
    async function _resolveDirSegments(dirHandle) {
        var root = await navigator.storage.getDirectory();
        if (typeof root.resolve === 'function') {
            var segs = await root.resolve(dirHandle);
            if (segs) return segs;
        }
        // Fallback: handle .name only (assume single-level under root, which
        // matches /join's per-run dir naming convention).
        return [dirHandle.name];
    }

    async function _readAllChunks(blob) {
        var chunks = [];
        var size = blob.size || 0;
        if (size === 0) return chunks;
        var offset = 0;
        while (offset < size) {
            var end = Math.min(offset + CHUNK_BYTES, size);
            var slice = blob.slice(offset, end);
            var ab = await slice.arrayBuffer();
            chunks.push(ab);
            offset = end;
        }
        return chunks;
    }

    async function _writeViaWorker(dirHandle, fileName, blob) {
        var segments = await _resolveDirSegments(dirHandle);
        var chunks = await _readAllChunks(blob);
        var worker = new Worker(_ensureWorkerUrl());
        return new Promise(function (resolve, reject) {
            var settled = false;
            worker.onmessage = function (e) {
                if (settled) return;
                settled = true;
                worker.terminate();
                var data = e.data || {};
                if (data.ok) resolve();
                else reject(new Error(data.error || 'opfs-writer: worker write failed'));
            };
            worker.onerror = function (err) {
                if (settled) return;
                settled = true;
                worker.terminate();
                reject(new Error('opfs-writer: worker error — ' + ((err && err.message) || 'unknown')));
            };
            try {
                worker.postMessage(
                    { type: 'writeBlob', dirSegments: segments, fileName: fileName, chunks: chunks },
                    chunks // transferables — zero-copy
                );
            } catch (err) {
                if (settled) return;
                settled = true;
                worker.terminate();
                reject(err);
            }
        });
    }

    async function _writeViaCreateWritable(dirHandle, fileName, blob) {
        var fh = await dirHandle.getFileHandle(fileName, { create: true });
        var ws = await fh.createWritable();
        await ws.write(blob);
        await ws.close();
    }

    // Public: writes a Blob into the given OPFS dirHandle under fileName.
    // After the write resolves, the caller can `await dirHandle.getFileHandle(fileName)`
    // → `await fh.getFile()` to get a stable File-handle for reading.
    async function writeBlob(dirHandle, fileName, blob) {
        if (!dirHandle || !fileName) throw new Error('opfs-writer: dirHandle and fileName required');
        if (!blob || typeof blob.slice !== 'function') throw new Error('opfs-writer: blob (Blob/File) required');
        var mode = _detectMode();
        if (mode === 'createWritable') {
            return _writeViaCreateWritable(dirHandle, fileName, blob);
        }
        if (mode === 'syncAccessHandle') {
            return _writeViaWorker(dirHandle, fileName, blob);
        }
        throw new Error('opfs-writer: no OPFS write API available');
    }

    w.__OpfsWriter = {
        __fromTimbricaShared: true,
        __version: '1.0.0',
        detectMode: _detectMode,
        writeBlob: writeBlob,
    };
})(window);
